import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import Footer from '../components/Footer';
import Header from '../components/Header';

const LandingPage = () => {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    const container = scrollRef.current;
  if (!container) return;

  const card = container.querySelector(".pricing-card");
  if (!card) return;

  const cardWidth = card.offsetWidth + 24; // gap
  container.scrollBy({
    left: direction * cardWidth,
    behavior: "smooth",
  });
  };
  return (
    <div className="landing-page">
      {/* Navigation */}
     <Header />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-container">
          <div className="hero-content">
            <h1 className="hero-title">
              Transform Your Documents into
              <span className="gradient-text"> Intelligent Chatbots</span>
            </h1>
            <p className="hero-subtitle">
              Upload your documents, train AI instantly, and deploy a real-time chatbot 
              on your website. Answer customer questions accurately using your own data.
            </p>
            <div className="hero-buttons">
              <Link to="/signup" className="btn btn-primary">Start Free Trial</Link>
              <Link to="/login" className="btn btn-secondary">View Demo</Link>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <div className="stat-number">10K+</div>
                <div className="stat-label">Documents Processed</div>
              </div>
              <div className="stat">
                <div className="stat-number">500+</div>
                <div className="stat-label">Businesses Trust Us</div>
              </div>
              <div className="stat">
                <div className="stat-number">99.9%</div>
                <div className="stat-label">Uptime</div>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <img src="/logo.png" alt="Figbot Logo" className="hero-logo" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <h2 className="section-title">Everything You Need to Deploy AI Chatbots</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📄</div>
              <h3>Document Upload</h3>
              <p>Upload PDF, DOCX, or TXT files. Support for up to 50 pages per document with automatic text extraction and OCR.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🧠</div>
              <h3>AI Training</h3>
              <p>RAG-based knowledge training. Your documents are converted into intelligent embeddings stored in a secure vector database.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚡</div>
              <h3>Real-Time Answers</h3>
              <p>Get instant, accurate responses based on your documents. Powered by advanced LLM technology with source citations.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔧</div>
              <h3>Easy Integration</h3>
              <p>One-line JavaScript embed code. Deploy your chatbot on any website in minutes. No technical expertise required.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Analytics Dashboard</h3>
              <p>Track user queries, view top questions, monitor usage, and identify areas for improvement with detailed insights.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Secure & Isolated</h3>
              <p>Each customer has a separate workspace with isolated data access. Enterprise-grade security and privacy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <h2 className="section-title">How Figbot Works</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Subscribe & Upload</h3>
              <p>Choose your plan and upload your documents through the dashboard.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>AI Training</h3>
              <p>Documents are processed, chunked, and converted into searchable knowledge.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Deploy</h3>
              <p>Get your embed code and add it to your website. Your chatbot is live!</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>Answer Questions</h3>
              <p>Users ask questions and get instant, accurate answers from your documents.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing">
  <div className="container">
    <h2 className="section-title">Simple, Transparent Pricing</h2>

    <div className="pricing-wrapper">
          {/* Left Arrow */}
          <button className="scroll-btn left" onClick={() => scroll(-1)}>‹</button>
    
    <div className="pricing-scroll">
      <div className="pricing-grid scrollable">
        
        {/* FREE */}
        <div className="pricing-card">
          <h3>Free</h3>
          <div className="price">
            <span className="currency">₹</span>
            <span className="amount">0</span>
            <span className="period">/month</span>
          </div>
          <ul className="pricing-features">
            <li>✓ 20 conversations / month</li>
            <li>✓ Upload PDFs up to 5 pages</li>
            <li>✓ Instant answers</li>
            <li>✓ No credit card required</li>
          </ul>
          <Link to="/signup" className="btn btn-outline">Get Started</Link>
        </div>

        {/* BASIC */}
        <div className="pricing-card">
          <h3>Basic</h3>
          <div className="price">
            <span className="currency">₹</span>
            <span className="amount">199</span>
            <span className="period">/month</span>
          </div>
          <ul className="pricing-features">
            <li>✓ 500 conversations / month</li>
            <li>✓ Upload PDFs up to 25 pages</li>
            <li>✓ Reliable response quality</li>
            <li>✓ Email support</li>
          </ul>
          <Link to="/signup" className="btn btn-outline">Get Started</Link>
        </div>

        {/* PRO */}
        <div className="pricing-card featured">
          <div className="badge">Most Popular</div>
          <h3>Pro</h3>
          <div className="price">
            <span className="currency">₹</span>
            <span className="amount">499</span>
            <span className="period">/month</span>
          </div>
          <ul className="pricing-features">
            <li>✓ 2,000 conversations / month</li>
            <li>✓ Upload PDFs up to 50 pages</li>
            <li>✓ Extended conversation continuity</li>
            <li>✓ Priority system access</li>
            <li>✓ Faster response handling</li>
          </ul>
          <Link to="/signup" className="btn btn-primary">Get Started</Link>
        </div>

        {/* PREMIUM */}
        <div className="pricing-card">
          <h3>Premium</h3>
          <div className="price">
            <span className="currency">₹</span>
            <span className="amount">999</span>
            <span className="period">/month</span>
          </div>
          <ul className="pricing-features">
            <li>✓ 6,000 conversations / month</li>
            <li>✓ Upload PDFs up to 300 pages</li>
            <li>✓ High-priority processing</li>
            <li>✓ Early access to new features</li>
          </ul>
          <Link to="/signup" className="btn btn-outline">Get Started</Link>
        </div>

        {/* ENTERPRISE */}
        <div className="pricing-card">
          <h3>Enterprise</h3>
          <div className="price">
            <span className="amount">Custom</span>
          </div>
          <ul className="pricing-features">
            <li>✓ Custom conversation limits</li>
            <li>✓ 500+ PDF page uploads</li>
            <li>✓ Multi-user access</li>
            <li>✓ Usage analytics</li>
            <li>✓ Dedicated support</li>
            <li>✓ Optional white-labeling</li>
          </ul>
          <Link to="/contact" className="btn btn-outline">Contact Sales</Link>
        </div>

      </div>
    </div>
      {/* Right Arrow */}
      <button className="scroll-btn right" onClick={() => scroll(1)}>
            ›
          </button>
    </div>
  </div>
      </section>


      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <h2>Ready to Transform Your Customer Support?</h2>
          <p>Join hundreds of businesses using Figbot to provide instant, accurate answers to their customers.</p>
          <Link to="/signup" className="btn btn-primary btn-large">Start Your Free Trial</Link>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;
